import { c as _c } from "react/compiler-runtime";
import type { Premiere } from "@filmweb/sanity-types";
import { format, isPast, isSameYear, isToday, parse } from "date-fns";
import { locale } from "src/lib/locale/date-fns";
;
import Button from "src/components/button/Button";
import WatchlistButton from "src/components/filmwebid/WatchlistButton";
import withWatchlistState from "src/components/filmwebid/withWatchlistState";
import CalendarSvg from "src/components/svg/CalendarSvg";
import { useProgramDatesContext, useSelectedCinemaContext } from "src/lib/contexts/ContextTypes";
import styles from './NoShows.module.css';
import classNames from 'classnames';
import { locale as loc } from "@locale";
import { formatLocaleString } from "src/lib/locale/helpers";
import getThemeStyles from "src/lib/styling/getThemeStyles";
import { defaultMovieIdentifierType } from "src/lib/filmwebid/useMovieIdentifier";

//#region [Props]
type NoShowsProps = {
  edi?: string | null;
  movieTitle: string;
  premiere?: Premiere | null;
  className?: string;
  /** the film has shows, but not at the selected cinemas */
  isFilteredByCinema?: boolean;
};
//#endregion

//#region [Component]
export default function NoShows(t0) {
  const $ = _c(36);
  const {
    edi,
    movieTitle,
    premiere,
    className,
    isFilteredByCinema: t1
  } = t0;
  const isFilteredByCinema = t1 === undefined ? false : t1;
  const datesContext = useProgramDatesContext();
  const cinemaContext = useSelectedCinemaContext();
  let desktopTextToShow = loc.program.noShows.noMoreShowsDefault;
  let mobileTextToShow;
  if ($[0] !== cinemaContext || $[1] !== datesContext || $[2] !== movieTitle || $[3] !== premiere) {
    let premiereParsed;
    if (premiere?.premiereDate || premiere?.reReleaseDate) {
      premiereParsed = parse(premiere.reReleaseDate ?? premiere.premiereDate, "yyyy-MM-dd", new Date());
    } else {
      if (premiere?.expectedPremiereDate) {
        premiereParsed = parse(premiere.expectedPremiereDate, "yyyy-MM-dd", new Date());
      }
    }
    if (premiere?.expectedPremiereDate) {
      const isThisYear = isSameYear(premiereParsed, new Date());
      desktopTextToShow = formatLocaleString(loc.program.noShows.expectedPremiere, {
        movieTitle,
        premiere: format(premiereParsed, isThisYear ? "MMMM" : "MMMM yyyy", {
          locale
        })
      });
      mobileTextToShow = <><strong>{formatLocaleString(loc.program.noShows.expectedPremiereMobile1, {
            premiere: format(premiereParsed, isThisYear ? "MMMM" : "MMMM yyyy", {
              locale
            })
          })}</strong>{loc.program.noShows.expectedPremiereMobile2}</>;
    } else {
      if (datesContext.availableDates?.[0] && !isToday(datesContext.availableDates[0])) {
        let t2;
        if ($[6] !== datesContext.availableDates[0]) {
          t2 = format(new Date(datesContext.availableDates[0]), "iiii d. MMMM", {
            locale
          });
          $[6] = datesContext.availableDates[0];
          $[7] = t2;
        } else {
          t2 = $[7];
        }
        let t3;
        if ($[8] !== cinemaContext || $[9] !== datesContext) {
          t3 = () => {
            datesContext.setSelectedDate(datesContext.availableDates[0]);
            cinemaContext.setSelectedCinemas(null);
          };
          $[8] = cinemaContext;
          $[9] = datesContext;
          $[10] = t3;
        } else {
          t3 = $[10];
        }
        let t4;
        if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = <CalendarSvg className={styles.SCalendarSvg} />;
          $[11] = t4;
        } else {
          t4 = $[11];
        }
        let t5;
        if ($[12] !== t2 || $[13] !== t3) {
          t5 = <>{loc.program.noShows.nextShow} <Button className={styles.SInlineDateSelector} text={t2} onClick={t3} icon={t4} inverse={true} /></>;
          $[12] = t2;
          $[13] = t3;
          $[14] = t5;
        } else {
          t5 = $[14];
        }
        desktopTextToShow = t5;
      } else {
        if (premiereParsed && isPast(premiereParsed) && (datesContext.availableDates?.length ?? 0) === 0) {
          desktopTextToShow = formatLocaleString(loc.program.noShows.premierePassed, {
            movieTitle,
            premiere: format(premiereParsed, "d. MMMM yyyy", {
              locale
            })
          });
          mobileTextToShow = <><strong>{formatLocaleString(loc.program.noShows.premierePassedMobile1, {
                premiere: format(premiereParsed, "d. MMMM yyyy", {
                  locale
                })
              })}</strong>{loc.program.noShows.premierePassedMobile2}</>;
        } else {
          if (premiere?.premiereDate) {
            const isThisYear_0 = isSameYear(premiereParsed, new Date());
            desktopTextToShow = formatLocaleString(loc.program.noShows.hasPremiere, {
              movieTitle,
              premiere: format(premiereParsed, isThisYear_0 ? "d. MMMM" : "d. MMMM yyyy", {
                locale
              })
            });
            const t2 = <strong>{formatLocaleString(loc.program.noShows.hasPremiereMobile1, {
                premiere: format(premiereParsed, isThisYear_0 ? "d. MMMM" : "d. MMMM yyyy", {
                  locale
                })
              })}</strong>;
            let t3;
            if ($[15] !== t2) {
              t3 = <>{t2}{loc.program.noShows.hasPremiereMobile2}</>;
              $[15] = t2;
              $[16] = t3;
            } else {
              t3 = $[16];
            }
            mobileTextToShow = t3;
          }
        }
      }
    }
    $[0] = cinemaContext;
    $[1] = datesContext;
    $[2] = movieTitle;
    $[3] = premiere;
    $[4] = desktopTextToShow;
    $[5] = mobileTextToShow;
  } else {
    desktopTextToShow = $[4];
    mobileTextToShow = $[5];
  }
  let t2;
  let t3;
  if ($[17] !== cinemaContext || $[18] !== className || $[19] !== isFilteredByCinema) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const themeStyles = getThemeStyles(styles);
      if (isFilteredByCinema) {
        let t4;
        if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = <div>{loc.program.noShows.noShowsForSelectedCinemas}</div>;
          $[22] = t4;
        } else {
          t4 = $[22];
        }
        let t5;
        if ($[23] !== cinemaContext) {
          t5 = <Button text={loc.program.noShows.showAllCinemas} onClick={() => cinemaContext.setSelectedCinemas(null)} inverse={true} />;
          $[23] = cinemaContext;
          $[24] = t5;
        } else {
          t5 = $[24];
        }
        t3 = <div className={classNames(styles.SNoShows, className, themeStyles)}>{t4}{t5}</div>;
        break bb0;
      }
      t2 = classNames(styles.SNoShows, className, themeStyles);
    }
    $[17] = cinemaContext;
    $[18] = className;
    $[19] = isFilteredByCinema;
    $[20] = t2;
    $[21] = t3;
  } else {
    t2 = $[20];
    t3 = $[21];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  let t4;
  if ($[25] !== edi) {
    t4 = !!edi && <WLWatchListButton movieIdentifier={{
      type: defaultMovieIdentifierType,
      value: edi
    }} className={styles.SWatchlistIcon} />;
    $[25] = edi;
    $[26] = t4;
  } else {
    t4 = $[26];
  }
  const t5 = mobileTextToShow ? mobileTextToShow : desktopTextToShow;
  let t6;
  if ($[27] !== t5) {
    t6 = <div className={styles.SMobileText}>{t5}</div>;
    $[27] = t5;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== desktopTextToShow) {
    t7 = <div className={styles.SDesktopText}>{desktopTextToShow}</div>;
    $[29] = desktopTextToShow;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  let t8;
  if ($[31] !== t2 || $[32] !== t4 || $[33] !== t6 || $[34] !== t7) {
    t8 = <div className={t2}>{t4}{t6}{t7}</div>;
    $[31] = t2;
    $[32] = t4;
    $[33] = t6;
    $[34] = t7;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  return t8;
}
//#endregion

const WLWatchListButton = withWatchlistState(WatchlistButton);